import qsa from "1dr-utils/helpers/elsForEach";
import extend from "extend";
import getParents from "@/js/libs/getParents";

// Private store for all the groups
let GROUPSTORES = {};

// For linking groups, basically radio stores that need
// co-validation
let RADIOSTORES = {};

let defaults = {
  className: "Formgroup-error",
  element: "span",
  validityMessage: false,
};

// Can be strings or functions
let defaultMessages = {
  email: "A valid email is required",
  text: "This field is required",
  select: "Please select one",
  number: function (field) {
    let min = field.getAttribute("min");
    let max = field.getAttribute("max");

    if (min && max) {
      return `Value must be between ${min} and ${max}`;
    }

    if (min) {
      return `Value must be at least ${min}`;
    }

    if (max) {
      return `Value must be under ${max}`;
    }

    return `Value must be a number`;
  },
};

// Helpers...

// this is really a 'get next' which helps to get the
// errors that will always be 'after' an element.
// Loop through the 'next element'
const _getSiblings = function (element, filter) {
  let sibling = element.nextElementSibling;
  let siblings = [];

  while (sibling) {
    if (!filter || sibling.classList.contains(filter)) {
      siblings.push(sibling);
    }

    sibling = sibling.nextElementSibling;
  }

  return siblings;
};

// generate a unique ID. Will just ever-increase in the life
// of the page and is not always for one thing.
const _newUid = (function () {
  let counter = 0;
  return function () {
    counter++;
    return counter;
  };
})();

let ErrorsGroup = function (elements, options) {
  this.group = "group-" + _newUid();
  GROUPSTORES[this.group] = this;
  this.collection = {};
  this.valid = false;

  qsa(elements, (el) => {
    this.add(el, options);
  });
};

ErrorsGroup.prototype.add = function (element, options) {
  let ie = new InputErrors(element, this.group, options);
  this.collection[ie.uid] = ie;

  if (ie.type === "radio") {
    // RADIOSTORES

    // Create a radio store for this name
    if (!RADIOSTORES[ie.inputName]) {
      RADIOSTORES[ie.inputName] = {};
      // It's the first, so set it as the 'controller'
      ie._fieldGroupLeader = true;
    }

    RADIOSTORES[ie.inputName][ie.uid] = ie;
    ie._fieldGroup = RADIOSTORES[ie.inputName];
  }

  return this;
};

ErrorsGroup.prototype.clearAll = function () {
  this.forEach((ie) => {
    ie.clear();
  });

  return this;
};

ErrorsGroup.prototype.focusFirstError = function () {
  for (let uid in this.collection) {
    if (!this.collection[uid]._isValid) {
      this.collection[uid].element.focus();
      break;
    }
  }
};

ErrorsGroup.prototype.getElement = function (uid) {
  return this.collection[uid];
};

ErrorsGroup.prototype.forEach = function (cb) {
  for (let uid in this.collection) {
    cb(this.collection[uid], this);
  }
};

// Pass true to report to show all errors.
ErrorsGroup.prototype.validate = function (report) {
  // All are true...
  let allIsValid = true;

  this.forEach((el) => {
    let isValid = el.validate(report);

    // Until one is not
    if (!isValid) {
      allIsValid = false;
    }
  });

  // Set state whether group all passed:
  this.valid = allIsValid;
  return allIsValid;
};

let InputErrors = function (el, groupId, options) {
  // Init with options, and save on the element:
  this.element = el;
  this.uid = "input-" + _newUid();
  this.options = extend({}, options, defaults);
  this.element.dataset.errorUid = this.uid;

  // Used for radios, by an ErrorGroup
  this._fieldGroup = false;
  this._fieldGroupLeader = false;

  // Store referenceable type:
  if (el.tagName === "INPUT") {
    this.type = el.getAttribute("type");
  } else {
    this.type = el.tagName.toLowerCase();
  }

  this.inputName = el.getAttribute("name");

  // Store message, do an initial state check for funsies:
  this.validityMessage =
    this.validityMessage ||
    defaultMessages[this.type] ||
    "This field is required";

  // Just assume nothing is valid, until a check is done:
  this._isValid = false;

  // Create a group to put it in:
  this.groupId = groupId;
  this.group = this.getGroup(); // might end up being false!

  if (typeof this.options.customValidation === "function") {
    this.customValidation = this.options.customValidation.bind(this);
  } else {
    this.customValidation = false;
  }

  // Surpress custom bubbles
  this.element.addEventListener("invalid", (e) => {
    e.preventDefault();
  });

  switch (this.element.tagName) {
    case "INPUT":
    case "SELECT":
    case "TEXTAREA":
    case "BUTTON":
      this.isFormField = true;
      break;

    default:
      this.isFormField = false;
  }

  // Store event listeners for reference
  this._eventListeners = {};
};

// WRITING, ADDING, CLEARING MESSAGES:
InputErrors.prototype.clear = function () {
  if (this._fieldGroup && !this._fieldGroupLeader) {
    return;
  }
  let siblings = _getSiblings(this._elForError(), this.options.className);
  siblings.forEach((el) => {
    el.remove();
  });

  this.element.removeAttribute("aria-describedby");

  return this;
};

InputErrors.prototype.link = function () {};

InputErrors.prototype.setCustomValidation = function (cb) {
  this.customValidation = cb.bind(this);
};

InputErrors.prototype._elForError = function () {
  if (this.type === "radio") {
    let parents = getParents(this.element, ".error-group");
    if (parents.length) {
      return parents[0];
    }
  }

  return this.element;
};

InputErrors.prototype.append = function (msg) {
  // Only allow the first to report messages on a fieldgroup.
  // If we need anything more complicated, then the _fieldGroup
  // is an object of all the radios in the error group.
  if (this._fieldGroup && !this._fieldGroupLeader) {
    return;
  }

  let n = document.createElement(this.options.element);
  n.id = this.uid + "_error";
  this.element.setAttribute("aria-describedby", n.id);

  let _msg;
  if (typeof msg === "function") {
    _msg = msg(this.element);
  } else {
    _msg = msg;
  }

  n.innerHTML = _msg;
  n.classList.add(this.options.className);
  this._elForError().after(n);
  return this;
};

InputErrors.prototype.replace = function (msg) {
  this.clear();
  this.append(msg);
  return this;
};

// CHECKING VALIDITY
InputErrors.prototype.validate = function (report) {
  // Will always be true if 'required' attribute not present.

  if (typeof report === "function") {
    // Use a custom function to check validity, and will not 'report'
    // if not valid. the function can dot hat manually by calling this.replace(msg)
    this._isValid = report(this.element, this);
    return this;
  } else if (typeof this.customValidation === "function") {
    this._isValid = this.customValidation(this.element);
  } else {
    if (this.isFormField) {
      this._isValid = this.element.checkValidity();
    } else {
      // Should provide a function if this isn't a form field,
      // or ignore validation and just set errors manually.
      this._isValid = true;
    }

    if (report && !this._isValid) {
      this.replace(this.validityMessage);
    } else if (report) {
      this.clear();
    }
  }

  return this._isValid;
};

// GROUP FUNCTIONS
InputErrors.prototype.getGroup = function () {
  if (this.groupId) {
    this.group = GROUPSTORES[this.groupId]; // could be undefined
    return this.group;
  } else {
    return false;
  }
};

InputErrors.prototype.clearAllInGroup = function () {
  let group = this.getGroup();
  if (group) {
    group.clearAll();
  }
  return this;
};

// EVENTS:
InputErrors.prototype.on = function (eventName, listener) {
  this.element.addEventListener(eventName, listener);
  return this;
};

// Relies on the browsers own ability to remove the 'same' listener:
InputErrors.prototype.off = function (eventName, listener) {
  this.element.removeEventListener(eventName, listener);
  return this;
};

export { ErrorsGroup, InputErrors };
